import { useQuery } from "@tanstack/react-query";

import { supabase } from "@/shared/lib/supabase";
import {
  LETTERS_DOC_TYPES,
  buildUserEstateMap,
  buildLatestActivityMap,
  assembleRow,
} from "@/shared/utils/lawyerDashboardHelpers";
import { formatRelativeTime } from "@/shared/utils/formatters";
import { useAppContext } from "@/shared/hooks/useAppContext";
import { useDemoMode } from "@/pages/demo/DemoModeContext";

import type {
  LawyerEstateRow,
  UserEstateRow,
  UserRow,
  EstateItemRow,
  EstateInvitationRow,
  EstateContactRow,
  ActivityLogRow,
} from "@/shared/types/lawyerDashboard";

export type { LawyerEstateRow, HighlightData } from "@/shared/types/lawyerDashboard";

async function fetchEnrichmentData(estateIds: number[]) {
  const [
    userEstatesResult,
    itemsResult,
    invitationsResult,
    contactsResult,
    activityResult,
    documentsResult,
    plaidResult,
  ] = await Promise.all([
    supabase
      .from("user_estates")
      .select(
        "estate_id, user_id, is_executor, is_administrator, is_trustee, is_nominated, relationship_to_decedent",
      )
      .in("estate_id", estateIds),
    supabase
      .from("estate_items")
      .select("estate_id, classification, balance")
      .in("estate_id", estateIds)
      .eq("is_hidden", false),
    supabase
      .from("estate_invitations")
      .select("estate_id, status, created_at")
      .in("estate_id", estateIds)
      .eq("status", "pending"),
    supabase
      .from("estate_contacts")
      .select("estate_id, relationship, email, phone")
      .in("estate_id", estateIds)
      .eq("is_hidden", false),
    supabase
      .from("activity_logs")
      .select("estate_id, action_type, metadata, created_at")
      .in("estate_id", estateIds)
      .order("created_at", { ascending: false }),
    supabase.from("estate_documents").select("estate_id, document_type").in("estate_id", estateIds),
    supabase
      .from("plaid_connections")
      .select("estate_id, status")
      .in("estate_id", estateIds)
      .eq("purpose", "estate_account"),
  ]);

  const results = [
    userEstatesResult,
    itemsResult,
    invitationsResult,
    contactsResult,
    activityResult,
    documentsResult,
  ];
  for (const r of results) {
    if (r.error) throw r.error;
  }

  return {
    userEstates: (userEstatesResult.data ?? []) as UserEstateRow[],
    items: (itemsResult.data ?? []) as EstateItemRow[],
    invitations: (invitationsResult.data ?? []) as EstateInvitationRow[],
    contacts: (contactsResult.data ?? []) as EstateContactRow[],
    activityLogs: (activityResult.data ?? []) as ActivityLogRow[],
    documents: (documentsResult.data ?? []) as { estate_id: number; document_type: string }[],
    plaidConnections: (plaidResult.data ?? []) as { estate_id: number; status: string }[],
  };
}

interface OwnerResult {
  ownerMap: Map<number, number>;
  userMap: Map<number, UserRow>;
}

async function fetchEstateOwners(estateIds: number[], accessToken: string): Promise<OwnerResult> {
  const ownerMap = new Map<number, number>();
  const userMap = new Map<number, UserRow>();

  const { data, error } = await supabase.functions.invoke("account/owners", {
    body: { estate_ids: estateIds.map(String) },
    headers: { Authorization: `Bearer ${accessToken}` },
  });

  if (error || !data?.owners) return { ownerMap, userMap };

  for (const entry of data.owners as {
    estate_id: string;
    owner: Record<string, unknown> | null;
  }[]) {
    if (!entry.owner) continue;
    const userId = entry.owner.user_id as number | undefined;
    if (!userId) continue;

    ownerMap.set(Number(entry.estate_id), userId);
    userMap.set(userId, {
      user_id: userId,
      full_name: (entry.owner.full_name as string) ?? null,
      first_name: (entry.owner.first_name as string) ?? null,
      last_name: (entry.owner.last_name as string) ?? null,
      email: (entry.owner.email as string) ?? null,
    });
  }

  return { ownerMap, userMap };
}

export function useLawyerPersons() {
  const { user, isLoading: userLoading } = useAppContext();
  const { isDemoMode } = useDemoMode();

  const query = useQuery({
    queryKey: ["lawyer-persons", user?.user_id],
    queryFn: async (): Promise<LawyerEstateRow[]> => {
      if (!user?.user_id) return [];

      const {
        data: { session },
      } = await supabase.auth.getSession();
      if (!session?.access_token) return [];

      const { data: lawyerEstates, error: lawyerError } = await supabase
        .from("lawyer_estates")
        .select("*, person:estates(*)")
        .eq("lawyer_user_id", user.user_id)
        .eq("status", "active")
        .order("assigned_at", { ascending: false });

      if (lawyerError) throw lawyerError;

      const { data: pendingClientInvitations } = await supabase
        .from("lawyer_client_invitations")
        .select("*")
        .eq("lawyer_user_id", user.user_id)
        .eq("status", "pending")
        .order("created_at", { ascending: false });

      const pendingRows: LawyerEstateRow[] = (pendingClientInvitations ?? []).map((inv) => ({
        id: `pending-invite-${inv.id}`,
        estateId: inv.estate_id ?? 0,
        pendingInvitationId: inv.id,
        assignedAt: inv.created_at,
        clientName: [inv.client_first_name, inv.client_last_name].filter(Boolean).join(" ") || null,
        clientEmail: inv.client_email,
        clientRole: null,
        clientRelationship: null,
        decedentName: null,
        dateOfDeath: null,
        proceedingType: "",
        authoritySuffix: "",
        highlight: {
          category: "action" as const,
          action: "resend_invite" as const,
          label: "Invite pending",
          nextStep: "Resend invite",
          timeAgo: inv.created_at ? formatRelativeTime(inv.created_at) : "",
          timestamp: inv.created_at ?? "",
        },
        inventoryTotal: 0,
        assetCount: 0,
        liabilityCount: 0,
        location: null,
      }));

      if (!lawyerEstates || lawyerEstates.length === 0) return pendingRows;

      const estateIds = lawyerEstates.map((le) => le.estate_id);

      const [enrichment, owners] = await Promise.all([
        fetchEnrichmentData(estateIds),
        fetchEstateOwners(estateIds, session.access_token),
      ]);

      const userEstateMap = buildUserEstateMap(enrichment.userEstates);
      const activityMap = buildLatestActivityMap(enrichment.activityLogs);

      const lettersEstateIds = new Set<number>();
      const docTypesByEstate = new Map<number, Set<string>>();
      for (const doc of enrichment.documents) {
        if (LETTERS_DOC_TYPES.includes(doc.document_type)) {
          lettersEstateIds.add(doc.estate_id);
        }
        const existing = docTypesByEstate.get(doc.estate_id);
        if (existing) {
          existing.add(doc.document_type);
        } else {
          docTypesByEstate.set(doc.estate_id, new Set([doc.document_type]));
        }
      }

      const plaidConnectionMap = new Map<number, { connected: boolean }>();
      for (const conn of enrichment.plaidConnections) {
        if (conn.status === "active") {
          plaidConnectionMap.set(conn.estate_id, { connected: true });
        }
      }

      const estateRows = lawyerEstates.map((le) =>
        assembleRow(le, {
          userEstateMap,
          ownerMap: owners.ownerMap,
          userMap: owners.userMap,
          items: enrichment.items,
          invitations: enrichment.invitations,
          contacts: enrichment.contacts,
          activityMap,
          docTypesByEstate,
          lettersEstateIds,
          plaidConnectionMap,
        }),
      );

      const pendingList = pendingClientInvitations ?? [];
      const pendingByEstateId = new Map<number, (typeof pendingList)[number]>();
      for (const inv of pendingList) {
        if (inv.estate_id && !pendingByEstateId.has(inv.estate_id)) {
          pendingByEstateId.set(inv.estate_id, inv);
        }
      }

      for (const row of estateRows) {
        const pendingInv = pendingByEstateId.get(row.estateId);
        if (pendingInv) {
          row.pendingInvitationId = pendingInv.id;
          row.clientName =
            [pendingInv.client_first_name, pendingInv.client_last_name].filter(Boolean).join(" ") ||
            row.clientName;
          row.clientEmail = pendingInv.client_email;
          row.highlight = {
            category: "action" as const,
            action: "resend_invite" as const,
            label: "Invite pending",
            nextStep: "Resend invite",
            timeAgo: pendingInv.created_at ? formatRelativeTime(pendingInv.created_at) : "",
            timestamp: pendingInv.created_at ?? "",
          };
        }
      }

      const estateIdSet = new Set(estateIds);
      const dedupedPendingRows = pendingRows.filter(
        (row) => !row.estateId || !estateIdSet.has(row.estateId),
      );

      return [...estateRows, ...dedupedPendingRows];
    },
    enabled: !!user?.user_id && !isDemoMode,
    staleTime: isDemoMode ? Infinity : 2 * 60 * 1000,
  });

  return {
    ...query,
    isLoading: userLoading || query.isLoading || (!!user?.user_id && !query.isFetched),
  };
}

export interface LawyerEstateNavItem {
  id: number;
  estateId: number;
  decedentName: string | null;
}

export function useLawyerEstateNav() {
  const { user, isLoading: userLoading } = useAppContext();
  const { isDemoMode } = useDemoMode();

  const query = useQuery({
    queryKey: ["lawyer-estate-nav", user?.user_id],
    queryFn: async (): Promise<LawyerEstateNavItem[]> => {
      if (!user?.user_id) return [];

      const { data, error } = await supabase
        .from("lawyer_estates")
        .select("id, estate_id, person:estates(full_name)")
        .eq("lawyer_user_id", user.user_id)
        .eq("status", "active")
        .order("assigned_at", { ascending: false });

      if (error) throw error;
      if (!data || data.length === 0) return [];

      const estateIds = data.map((le) => le.estate_id);
      const { data: intakeInvites } = await supabase
        .from("lawyer_client_invitations")
        .select("estate_id, client_first_name, client_last_name")
        .in("estate_id", estateIds)
        .eq("status", "pending");

      const intakeMap = new Map(
        (intakeInvites ?? []).map((inv) => [
          inv.estate_id,
          [inv.client_first_name, inv.client_last_name].filter(Boolean).join(" "),
        ]),
      );

      return data.map((le) => {
        const estate = Array.isArray(le.person) ? le.person[0] : le.person;
        const fullName = estate?.full_name as string | null;
        return {
          id: le.id,
          estateId: le.estate_id,
          decedentName: fullName ?? intakeMap.get(le.estate_id) ?? null,
        };
      });
    },
    enabled: !!user?.user_id && !isDemoMode,
    staleTime: isDemoMode ? Infinity : 5 * 60 * 1000,
  });

  return {
    ...query,
    isLoading: userLoading || query.isLoading || (!!user?.user_id && !query.isFetched),
  };
}
